import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { useIsMobile } from '../ui/use-mobile';
import { bookingBrandFontFamilyVar, bookingBrandInfoTintCss, teboxDesignSystem, useBookingTebox } from '../../styles/tebox-booking';
import { TeeSheetPlayerCell } from './TeeSheetPlayerCell';

interface TeeTime {
  id: string;
  courseName: string;
  courseId: string;
  dateTime: Date;
  originalDate?: string; // Original date string from backend (YYYY-MM-DD) - made optional
  price: number;
  capacity: number;
  bookedCount: number;
  availableSpots: number;
  courseColor?: string;
  pricingOptions?: Array<{
    customerTypeId: string;
    customerTypeName: string;
    price: number;
    color: string;
  }>;
  memberPlayers?: Array<{
    spotNumber: number;
    firstName: string;
    lastName: string;
    customerType: string;
  }>;
  dynamicPricing?: {
    direction: 'lower' | 'higher';
    percentRounded: number;
    underOnePercent: boolean;
  };
}

interface BrandColors {
  fontFamily: string;
  textColor: string;
  secondaryColor: string;
  primaryColor?: string;
}

interface TeeSheetViewProps {
  teeTimes: TeeTime[];
  selectedDate: Date;
  onTeeTimeSelect: (teeTime: TeeTime) => void;
  selectedTeeTimeId?: string;
  isMember: boolean;
  blockedTeeTimes?: Set<string>;
  brandColors?: BrandColors;
}

const TeeSheetView: React.FC<TeeSheetViewProps> = ({
  teeTimes,
  selectedDate,
  onTeeTimeSelect,
  selectedTeeTimeId,
  isMember,
  blockedTeeTimes = new Set(),
  brandColors: brandColorsProp,
}) => {
  const tebox = useBookingTebox();
  const brandColors = brandColorsProp ?? {
    fontFamily: 'Inter',
    textColor: tebox.colors.textPrimary,
    secondaryColor: tebox.colors.textSecondary
  };
  const isMobile = useIsMobile();
  const maxGolfers = 4;
  const isExpandedView = true;
  const accentSelect = teboxDesignSystem.colors.accent.primaryDarker;
  /** Lighter than structural borders — list row separators only */
  const rowDivider = 'rgba(4, 42, 43, 0.07)';

  const spotsTintBackground = bookingBrandInfoTintCss(brandColors.primaryColor);
  const spotsBadgeShellStyle: React.CSSProperties = {
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: teboxDesignSystem.spacing.sm,
    padding: `${teboxDesignSystem.spacing.xs} ${teboxDesignSystem.spacing.sm}`,
    borderRadius: teboxDesignSystem.borderRadius.sm,
    backgroundColor: spotsTintBackground,
    border: `1px solid ${teboxDesignSystem.colors.border.light}`,
    boxSizing: 'border-box',
    maxWidth: '100%',
  };
  const spotsBadgeValueStyle: React.CSSProperties = {
    fontFamily: bookingBrandFontFamilyVar,
    fontWeight: 500,
    fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
    lineHeight: 1.2,
    fontVariantNumeric: 'tabular-nums',
    color: brandColors.textColor,
    minWidth: 0,
  };

  const distinctCourseCount = useMemo(
    () => new Set(teeTimes.map((t) => t.courseId)).size,
    [teeTimes]
  );

  // Group tee times by hour for better organization
  const groupedTeeTimes = useMemo(() => {
    const groups: { [hour: string]: TeeTime[] } = {};
    
    teeTimes.forEach(teeTime => {
      const hour = format(teeTime.dateTime, 'HH');
      if (!groups[hour]) {
        groups[hour] = [];
      }
      groups[hour].push(teeTime);
    });
    
    // Sort each group by minute
    Object.keys(groups).forEach(hour => {
      groups[hour].sort((a, b) => a.dateTime.getMinutes() - b.dateTime.getMinutes());
    });
    
    return groups;
  }, [teeTimes]);

  /** 12-hour label for UI (matches TeeTimeCard / PricingModal). */
  const getTimeDisplay = (dateTime: Date) => format(dateTime, 'h:mm a');

  /** Local 24h HH:mm for blocked-slot keys (must stay aligned with tee time instants). */
  const getTimeBlockKey = (dateTime: Date) => format(dateTime, 'HH:mm');

  const formatHourBandLabel = (hourZeroPadded: string) =>
    format(new Date(2020, 0, 1, parseInt(hourZeroPadded, 10), 0), 'h a');

  // Mobile text-only list view
  if (isMobile) {
    return (
      <div className="w-full h-full bg-white rounded-[6px] overflow-hidden">
        <div className="h-full overflow-y-auto">
          {Object.keys(groupedTeeTimes).length === 0 ? (
            <div className="flex items-center justify-center h-full text-tebox-muted p-4">
              <p className="text-center" style={{ fontFamily: bookingBrandFontFamilyVar }}>
                No tee times available for this date
              </p>
            </div>
          ) : (
            <div className="divide-y divide-tebox-border">
              {Object.keys(groupedTeeTimes)
                .sort()
                .map(hour => (
                  <div key={hour}>
                    {/* Hour Header */}
                    <div className="sticky top-0 bg-tebox-highlight px-4 py-2 z-10">
                      <span
                        className="text-tebox-body font-medium text-tebox-ink"
                        style={{ fontFamily: bookingBrandFontFamilyVar }}
                      >
                        {formatHourBandLabel(hour)}
                      </span>
                    </div>

                    {/* Tee Times List */}
                    {groupedTeeTimes[hour].map(teeTime => {
                      const timeStr = getTimeDisplay(teeTime.dateTime);
                      const blockKey = getTimeBlockKey(teeTime.dateTime);
                      const allSpotKeys = Array.from({length: maxGolfers}, (_, i) => i + 1).map(
                        (spot) => `${blockKey}-${spot}`
                      );
                      const isBlocked = allSpotKeys.every(key => blockedTeeTimes.has(key));
                      const memberPlayers = teeTime.memberPlayers || [];
                      const bookedCount = memberPlayers.length;
                      
                      return (
                        <div
                          key={teeTime.id}
                          onClick={() => !isBlocked && onTeeTimeSelect(teeTime)}
                          style={{
                            padding: '16px',
                            borderLeft: `4px solid ${selectedTeeTimeId === teeTime.id ? accentSelect : isBlocked ? tebox.colors.textTertiary : teeTime.courseColor || tebox.colors.textSecondary}`,
                            backgroundColor:
                              selectedTeeTimeId === teeTime.id
                                ? teboxDesignSystem.colors.background.highlight
                                : teboxDesignSystem.colors.background.primary,
                            cursor: isBlocked ? 'not-allowed' : 'pointer',
                            opacity: isBlocked ? 0.6 : 1,
                            transition: 'background-color 0.2s ease'
                          }}
                        >
                          <div className="flex items-start gap-4">
                            {/* Left side: Time and Status */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 mb-2">
                                <span 
                                  style={{
                                    fontSize: teboxDesignSystem.typography.body.fontSize,
                                    fontWeight: teeTime.dateTime.getMinutes() === 0 ? 500 : 400,
                                    color: brandColors.textColor,
                                    fontFamily: bookingBrandFontFamilyVar
                                  }}
                                >
                                  {timeStr}
                                </span>
                                {isBlocked && (
                                  <span 
                                    style={{
                                      fontSize: teboxDesignSystem.typography.caption.fontSize,
                                      color: brandColors.secondaryColor,
                                      fontWeight: '500',
                                      padding: '2px 8px',
                                      backgroundColor: tebox.colors.bgSecondary,
                                      borderRadius: 'var(--brand-border-radius, 6px)',
                                      fontFamily: bookingBrandFontFamilyVar
                                    }}
                                  >
                                    Blocked
                                  </span>
                                )}
                              </div>
                              
                              {/* Course name + spots (same row when no player chips) */}
                              {memberPlayers.length > 0 ? (
                                <>
                                  {teeTime.courseName ? (
                                    <div
                                      style={{
                                        fontSize: teboxDesignSystem.typography.body.fontSize,
                                        color: brandColors.secondaryColor,
                                        marginBottom: '4px',
                                        fontFamily: bookingBrandFontFamilyVar,
                                      }}
                                    >
                                      {teeTime.courseName}
                                    </div>
                                  ) : null}
                                  <div style={{ marginTop: '8px' }}>
                                  {!isMember ? (
                                    <div 
                                      style={{
                                        fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                                        color: brandColors.textColor,
                                        fontWeight: '500',
                                        marginBottom: '4px',
                                        fontFamily: bookingBrandFontFamilyVar
                                      }}
                                    >
                                      Players ({bookedCount}/{maxGolfers}):
                                    </div>
                                  ) : null}
                                  <div className="flex flex-wrap gap-2">
                                    {memberPlayers.map((player, idx) => {
                                      // Determine display name (same logic as PlayerCell)
                                      const isGuest = player.customerType === 'Guest' || (player.firstName === 'Guest' && !player.lastName);
                                      const isPrivacyEnabled = player.firstName === 'Member' && !player.lastName && player.customerType !== 'Guest';
                                      const isNonMemberCustomer = !isGuest && !isPrivacyEnabled && !player.lastName && 
                                                                player.customerType && player.customerType !== 'Guest' &&
                                                                player.firstName === player.customerType;
                                      const shouldShowMemberName = isMember && !isPrivacyEnabled && !isGuest && !isNonMemberCustomer && 
                                                                 !!player.firstName;
                                      
                                      let displayName: string;
                                      if (isGuest) {
                                        displayName = 'Guest';
                                      } else if (isPrivacyEnabled) {
                                        displayName = 'Member';
                                      } else if (isNonMemberCustomer) {
                                        displayName = player.customerType || player.firstName || 'Guest';
                                      } else if (shouldShowMemberName) {
                                        displayName = [player.firstName, player.lastName].filter(Boolean).join(' ').trim();
                                      } else {
                                        displayName = player.customerType || player.firstName || 'Guest';
                                      }
                                      
                                      return (
                                        <span
                                          key={idx}
                                          style={{
                                            fontSize: teboxDesignSystem.typography.caption.fontSize,
                                            color: brandColors.textColor,
                                            padding: '4px 8px',
                                            backgroundColor: tebox.colors.bgHighlight,
                                            border: `1px solid ${tebox.colors.border}`,
                                            borderRadius: 'var(--brand-border-radius, 6px)',
                                            fontFamily: bookingBrandFontFamilyVar
                                          }}
                                        >
                                          {displayName} {player.customerType && displayName !== player.customerType ? `(${player.customerType})` : ''}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                                </>
                              ) : (
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-start',
                                    gap: 8,
                                    width: '100%',
                                    boxSizing: 'border-box',
                                  }}
                                >
                                  {teeTime.courseName ? (
                                    <div
                                      style={{
                                        flex: 1,
                                        minWidth: 0,
                                        fontSize: teboxDesignSystem.typography.body.fontSize,
                                        color: brandColors.secondaryColor,
                                        fontFamily: bookingBrandFontFamilyVar,
                                      }}
                                    >
                                      {teeTime.courseName}
                                    </div>
                                  ) : (
                                    <div style={{ flex: 1, minWidth: 0 }} />
                                  )}
                                  {!isMember ? (
                                    <div
                                      style={{ ...spotsBadgeShellStyle, flexShrink: 0 }}
                                      aria-label={
                                        teeTime.availableSpots > 0
                                          ? `${teeTime.availableSpots} spots available`
                                          : 'Full'
                                      }
                                    >
                                      <span style={spotsBadgeValueStyle}>
                                        {teeTime.availableSpots > 0
                                          ? `${teeTime.availableSpots} spot${teeTime.availableSpots !== 1 ? 's' : ''} available`
                                          : 'Full'}
                                      </span>
                                    </div>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full h-full overflow-hidden rounded-[6px]"
      style={{
        backgroundColor: teboxDesignSystem.colors.background.primary,
      }}
    >
      <div
        className="h-full overflow-y-auto"
        style={
          Object.keys(groupedTeeTimes).length > 0
            ? {
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
              }
            : undefined
        }
      >
        {Object.keys(groupedTeeTimes).length === 0 ? (
          <div
            className="flex items-center justify-center h-full p-6"
            style={{
              fontFamily: bookingBrandFontFamilyVar,
              fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
              color: tebox.colors.textSecondary,
            }}
          >
            No tee times available for this date
          </div>
        ) : (
          <>
            <div
              style={{
                fontFamily: bookingBrandFontFamilyVar,
                fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                fontWeight: 500,
                color: teboxDesignSystem.colors.text.secondary,
                padding: `${teboxDesignSystem.spacing.sm} ${teboxDesignSystem.spacing.md}`,
                borderBottom: `1px solid ${rowDivider}`,
                backgroundColor: teboxDesignSystem.colors.background.primary,
              }}
            >
              {format(selectedDate, 'EEEE, MMM d, yyyy')}
            </div>
            {Object.keys(groupedTeeTimes)
              .sort()
              .map((hour) => (
                <section
                  key={hour}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                  }}
                >
                  <header
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      padding: `${teboxDesignSystem.spacing.sm} ${teboxDesignSystem.spacing.md}`,
                      backgroundColor: teboxDesignSystem.colors.background.secondary,
                      borderBottom: `1px solid ${rowDivider}`,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: bookingBrandFontFamilyVar,
                        fontWeight: 500,
                        fontSize: teboxDesignSystem.typography.body.fontSize,
                        color: teboxDesignSystem.colors.text.primary,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {formatHourBandLabel(hour)}
                    </span>
                  </header>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0,
                    }}
                  >
                    {groupedTeeTimes[hour].map((teeTime) => {
                      const timeStr = getTimeDisplay(teeTime.dateTime);
                      const blockKey = getTimeBlockKey(teeTime.dateTime);
                      const allSpotKeys = Array.from({ length: maxGolfers }, (_, i) => i + 1).map(
                        (spot) => `${blockKey}-${spot}`
                      );
                      const isBlocked = allSpotKeys.every((key) => blockedTeeTimes.has(key));
                      const isSelected = selectedTeeTimeId === teeTime.id;
                      const spotsLabel = isBlocked
                        ? 'Blocked'
                        : teeTime.availableSpots > 0
                          ? `${teeTime.availableSpots} spot${teeTime.availableSpots !== 1 ? 's' : ''}`
                          : 'Full';
                      let rowAriaLabel = `${teeTime.courseName ?? 'Tee time'} at ${timeStr}`;
                      if (isBlocked) rowAriaLabel += ', blocked';
                      else if (!isMember) {
                        rowAriaLabel += `, ${spotsLabel}`;
                      }

                      return (
                        <div
                          key={teeTime.id}
                          role="button"
                          tabIndex={isBlocked ? -1 : 0}
                          aria-disabled={isBlocked}
                          aria-label={rowAriaLabel}
                          onClick={() => !isBlocked && onTeeTimeSelect(teeTime)}
                          onKeyDown={(e) => {
                            if (isBlocked) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onTeeTimeSelect(teeTime);
                            }
                          }}
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'stretch',
                            gap: teboxDesignSystem.spacing.md,
                            padding: `${teboxDesignSystem.spacing.sm} ${teboxDesignSystem.spacing.md}`,
                            borderRadius: 6,
                            border: 'none',
                            borderBottom: `1px solid ${rowDivider}`,
                            backgroundColor: isSelected
                              ? teboxDesignSystem.colors.background.highlight
                              : teboxDesignSystem.colors.background.primary,
                            boxShadow: 'none',
                            cursor: isBlocked ? 'not-allowed' : 'pointer',
                            opacity: isBlocked ? 0.62 : 1,
                            transition: 'background-color 0.15s ease',
                            boxSizing: 'border-box',
                          }}
                          onMouseEnter={(e) => {
                            if (isBlocked || isSelected) return;
                            e.currentTarget.style.backgroundColor =
                              teboxDesignSystem.colors.background.hover;
                          }}
                          onMouseLeave={(e) => {
                            if (isBlocked) return;
                            e.currentTarget.style.backgroundColor = isSelected
                              ? teboxDesignSystem.colors.background.highlight
                              : teboxDesignSystem.colors.background.primary;
                          }}
                        >
                          <div
                            style={{
                              width: 88,
                              flexShrink: 0,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              gap: teboxDesignSystem.spacing.xs,
                            }}
                          >
                            <span
                              style={{
                                fontFamily: bookingBrandFontFamilyVar,
                                fontWeight: 500,
                                fontSize: teboxDesignSystem.typography.body.fontSize,
                                lineHeight: 1.15,
                                fontVariantNumeric: 'tabular-nums',
                                color: brandColors.textColor,
                              }}
                            >
                              {timeStr}
                            </span>
                          </div>

                          <div
                            style={{
                              flex: distinctCourseCount > 1 ? '0 1 200px' : '0 1 160px',
                              minWidth: 0,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              gap: 4,
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: teboxDesignSystem.spacing.sm,
                                minWidth: 0,
                                width: '100%',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: teboxDesignSystem.spacing.sm,
                                  minWidth: 0,
                                  flex: 1,
                                }}
                              >
                              <span
                                title={
                                  teeTime.courseName
                                    ? `Course: ${teeTime.courseName}`
                                    : 'Course color'
                                }
                                aria-hidden
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: teboxDesignSystem.borderRadius.full,
                                  backgroundColor:
                                    teeTime.courseColor || tebox.colors.textSecondary,
                                  flexShrink: 0,
                                }}
                              />
                              {teeTime.courseName ? (
                                <div
                                  title={teeTime.courseName}
                                  style={{
                                    fontFamily: bookingBrandFontFamilyVar,
                                    fontWeight: 500,
                                    fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                                    lineHeight: 1.35,
                                    color: teboxDesignSystem.colors.text.secondary,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    minWidth: 0,
                                    flex: 1,
                                  }}
                                >
                                  {teeTime.courseName}
                                </div>
                              ) : (
                                <span
                                  style={{
                                    fontFamily: bookingBrandFontFamilyVar,
                                    fontSize: teboxDesignSystem.typography.caption.fontSize,
                                    color: teboxDesignSystem.colors.text.tertiary,
                                  }}
                                >
                                  Course
                                </span>
                              )}
                              </div>
                              {!isMember ? (
                                <div
                                  style={{
                                    ...spotsBadgeShellStyle,
                                    gap: 6,
                                    flexShrink: 0,
                                  }}
                                  aria-label={spotsLabel}
                                >
                                  {isBlocked ? (
                                    <svg
                                      width="12"
                                      height="12"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                      aria-hidden
                                    >
                                      <path
                                        d="M18 8H17V6C17 3.24 14.76 1 12 1S7 3.24 7 6V8H6C4.9 8 4 8.9 4 10V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V10C20 8.9 19.1 8 18 8ZM12 17C10.9 17 10 16.1 10 15S10.9 13 12 13S14 13.9 14 15S13.1 17 12 17ZM15.1 8H8.9V6C8.9 4.29 10.29 2.9 12 2.9S15.1 4.29 15.1 6V8Z"
                                        fill={tebox.colors.textSecondary}
                                    />
                                  </svg>
                                ) : null}
                                  <span
                                    style={{
                                      ...spotsBadgeValueStyle,
                                      fontSize: teboxDesignSystem.typography.caption.fontSize,
                                      fontWeight: 500,
                                      color: isBlocked ? tebox.colors.textSecondary : brandColors.textColor,
                                    }}
                                  >
                                    {spotsLabel}
                                  </span>
                                </div>
                              ) : null}
                            </div>
                          </div>

                          <div
                            style={{
                              flex: 1,
                              minWidth: 0,
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'stretch',
                              gap: teboxDesignSystem.spacing.sm,
                            }}
                          >
                            {Array.from({ length: maxGolfers }, (_, i) => i + 1).map((playerIndex) => (
                              <div key={playerIndex} style={{ flex: 1, minWidth: 0 }}>
                                <TeeSheetPlayerCell
                                  playerIndex={playerIndex}
                                  teeTime={teeTime}
                                  isExpandedView={isExpandedView}
                                  isBlocked={isBlocked}
                                  brandColors={brandColors}
                                  onCellClick={(e) => {
                                    e.stopPropagation();
                                    if (!isBlocked) {
                                      onTeeTimeSelect(teeTime);
                                    }
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
          </>
        )}
      </div>
    </div>
  );
};

export default TeeSheetView;
